import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/api';
import { FaUsers, FaBriefcase, FaChartBar, FaSearch, FaUserPlus } from 'react-icons/fa';
import { StatCard, SectionHeader, ActivityListItem } from '../components/DashboardComponents';
import { Link } from 'react-router-dom';

function BrokerOverview() {
    const { user } = useAuth();
    const [stats, setStats] = useState({ rosterCount: 0, activeContracts: 0, winRate: '75%' });
    const [loading, setLoading] = useState(true);

    const fetchStats = useCallback(async () => {
        if (!user?.profileId) return;
        try {
            const rosterRes = await apiClient.get('/broker/roster');
            setStats(prev => ({
                ...prev,
                rosterCount: rosterRes.data?.length || 0,
                activeContracts: 0 // Fetch real contracts if endpoint exists
            }));
        } catch (err) {
            console.error("Failed to fetch broker stats:", err);
        }
    }, [user]);

    useEffect(() => {
        setLoading(true);
        fetchStats().finally(() => setLoading(false));
    }, [fetchStats]);

    return (
        <div className="space-y-10 animate-fade-in font-sans">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-800 tracking-tight">Agent Overview</h1>
                    <p className="text-slate-500 mt-1">Manage your team and track performance.</p>
                </div>
                <div className="flex gap-3">
                    <Link to="/broker-dashboard/find-workers" className="bg-slate-800 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-900 transition-all flex items-center shadow-lg shadow-slate-200">
                        <FaSearch className="mr-2" /> Find Workers
                    </Link>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <StatCard 
                    title="Workers in Roster" 
                    value={loading ? '...' : stats.rosterCount} 
                    icon={FaUsers} 
                    color="blue" 
                />
                <StatCard 
                    title="Active Contracts" 
                    value={loading ? '...' : stats.activeContracts} 
                    icon={FaBriefcase} 
                    color="purple" 
                />
                <StatCard 
                    title="Agency Win Rate" 
                    value={stats.winRate} 
                    icon={FaChartBar} 
                    color="green" 
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main: Recent Activity */}
                <div className="lg:col-span-2 bg-white rounded-[2rem] shadow-sm border border-slate-100 p-8">
                    <SectionHeader title="Recent Activity" />
                    <div className="space-y-4">
                        {/* Mock data until backend logging implemented */}
                        <ActivityListItem 
                            title="System Note" 
                            subtitle="Welcome to your new agent dashboard." 
                            date="Just now" 
                            status="INFO" 
                            statusColor="bg-blue-50 text-blue-600" 
                        />
                        <div className="text-center py-10 opacity-50">
                            <FaUserPlus className="mx-auto text-4xl mb-3 text-slate-200" />
                            <p className="text-slate-400 font-medium">Invite workers to see their activity here.</p>
                        </div>
                    </div>
                </div>

                {/* Sidebar: Quick Tips */}
                <div className="lg:col-span-1 space-y-8">
                    <div className="bg-emerald-900 rounded-[2rem] p-8 text-white relative overflow-hidden shadow-xl shadow-emerald-100">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mt-8"></div>
                        <h3 className="text-xl font-bold mb-4 relative z-10">Agent Tip</h3>
                        <p className="text-emerald-100 text-sm leading-relaxed relative z-10">
                            Keep your roster active by regularly checking for new worker applications. Verified workers increase your win rate.
                        </p>
                        <button className="mt-6 bg-white text-emerald-900 px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider hover:bg-emerald-50 transition-colors">
                            Learn More
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BrokerOverview;